import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, TrendingUp, RefreshCw, Radio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";

export default function GoldRatesPage() {
  const { user } = useAuth();
  const [rates, setRates] = useState([]);
  const [f, setF] = useState({ metal: "gold", purity: "22K", rate_per_gram: "" });
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const canEdit = can(user, "gold_rates.edit");

  const load = async () => {
    const { data } = await api.get("/gold-rates");
    setRates(data);
    // derive last arihant sync
    const arihantRates = (data || []).filter(r => r.source === "arihantspot");
    if (arihantRates.length) {
      const latest = arihantRates.reduce((a, b) => new Date(a.updated_at) > new Date(b.updated_at) ? a : b);
      setLastSync(latest.updated_at);
    } else {
      setLastSync(null);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.purity || !f.rate_per_gram) return toast.error("Purity & rate required");
    try {
      await api.post("/gold-rates", { ...f, rate_per_gram: Number(f.rate_per_gram) });
      toast.success("Rate updated — product prices recalculated");
      setF({ ...f, rate_per_gram: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const syncArihant = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/gold-rates/sync-arihant");
      toast.success(`Synced ${data.rates.length} rate${data.rates.length === 1 ? "" : "s"} from Arihant Spot`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6" data-testid="gold-rates-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Catalog</div>
          <h1 className="font-serif text-3xl lg:text-4xl font-medium">Metal Rates</h1>
          <p className="text-[#4A5A50] mt-1">Updating a rate here instantly recalculates all gold-rate-based product prices.</p>
        </div>
        {canEdit && (
          <Button
            onClick={syncArihant}
            disabled={syncing}
            className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white"
            data-testid="sync-arihant-btn"
          >
            {syncing ? <Loader2 size={14} className="animate-spin mr-2"/> : <RefreshCw size={14} className="mr-2"/>}
            {syncing ? "Syncing…" : "Sync from Arihant Spot"}
          </Button>
        )}
      </div>

      {/* Live source card */}
      <Card className="border-[#E4DFCF] rounded-xl bg-gradient-to-br from-[#F1EDDF] to-white">
        <CardContent className="p-5 flex items-center gap-4 flex-wrap">
          <div className="w-11 h-11 rounded-full bg-[#0F5E39] text-white flex items-center justify-center shrink-0">
            <Radio size={20} strokeWidth={1.6}/>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50]">Live source</div>
            <div className="font-serif text-lg">Arihant Spot · Mumbai Bullion</div>
            <div className="text-xs text-[#4A5A50] mt-0.5">
              Pulls 995 & 999 gold + silver rates from <a href="https://www.arihantspot.in/" target="_blank" rel="noreferrer" className="underline text-[#0F5E39]">arihantspot.in</a> and computes 24K/22K/18K per gram.
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50]">Last synced</div>
            <div className="text-sm font-medium text-[#0F1E15]" data-testid="last-sync-time">
              {lastSync ? format(new Date(lastSync), "dd MMM · HH:mm") : "Never"}
            </div>
          </div>
        </CardContent>
      </Card>

      {canEdit && (
        <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-3">Manual override</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label>Metal</Label>
              <Select value={f.metal} onValueChange={(v)=>setF({...f, metal:v})}>
                <SelectTrigger className="mt-1" data-testid="rate-metal-select"><SelectValue/></SelectTrigger>
                <SelectContent>{["gold","silver","platinum","diamond","other"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Purity</Label>
              <Input value={f.purity} onChange={(e)=>setF({...f, purity:e.target.value})} placeholder="22K / 950 / 92.5" className="mt-1" data-testid="rate-purity-input"/>
            </div>
            <div>
              <Label>Rate per gram (₹)</Label>
              <Input type="number" value={f.rate_per_gram} onChange={(e)=>setF({...f, rate_per_gram:e.target.value})} className="mt-1" data-testid="rate-input"/>
            </div>
            <Button onClick={save} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="save-rate-btn"><Save size={14} className="mr-1"/> Save rate</Button>
          </div>
        </CardContent></Card>
      )}

      <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-3">Current rates</div>
        {rates.length === 0 ? (
          <div className="text-sm text-[#4A5A50]">No rates configured yet. Click <strong>Sync from Arihant Spot</strong> to pull live rates.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {rates.map(r => (
              <div key={r.id} className="p-4 rounded-xl border border-[#E4DFCF] bg-white flex items-center justify-between" data-testid={`rate-row-${r.metal}-${r.purity}`}>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{r.metal} · {r.purity}</div>
                  <div className="font-serif text-2xl text-[#6E5416] mt-1">₹{r.rate_per_gram.toLocaleString('en-IN')}/g</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-semibold ${r.source === "arihantspot" ? "bg-[#E1F0E6] text-[#0F5E39]" : "bg-[#F1EDDF] text-[#4A5A50]"}`}>
                      {r.source === "arihantspot" ? "LIVE" : "MANUAL"}
                    </span>
                    <span className="text-[10px] text-[#4A5A50]">{format(new Date(r.updated_at), "dd MMM, HH:mm")}</span>
                  </div>
                </div>
                <TrendingUp size={20} className="text-[#C9A24B]"/>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}
